import React, { useContext, useEffect, useRef, useState } from 'react';
import ProfileForm from './components/profile';
import { FormTitle } from './form.styles';
import { Background, Card } from "../../../components/shared-styles/shared-styles";
import Education from './components/educations';
import Experience from './components/experiences';
import Projects from './components/projects';
import Certifications from './components/certifications';
import { useUpdateEducations,useUpdateExperiences,useUpdateSkills,useUpdateProjects,useUpdateCertifications } from '../../../services/api/portfolio-api'; // Import the custom hook
import { useGetFullPortfolio,useUpdateProfile,useUpdateOnboardingStep } from '../../../services/api/users-api'; // Import the new hook
import { useNavigate } from 'react-router-dom';
import Skills from './components/skills';
import Loading from '../../../components/loading';
import { ToastContext } from '../../../services/contexts/toast-context';
import { useUser } from '../../../services/contexts/user-context';

const Form = () => {

  const navigate = useNavigate()
  const toastRef = useContext(ToastContext);
  const { user } = useUser();
  const [resumeInfo, setResumeInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState('profile');
  const [profileId, setProfileId] = useState(null);
  const hasLoadedInitialPortfolio = useRef(false);

  const { mutate: updateProfile, isPending: isUpdatingProfile, error: updateError } = useUpdateProfile();
  const { mutate: updateEducation, isPending: isUpdatingEducation, error: updateEducationError } = useUpdateEducations();
  const { mutate: updateExperience, isPending: isUpdatingExperience, error: updateExperienceError } = useUpdateExperiences();
  const { mutate: updateProjects, isPending: isUpdatingProjects, error: updateProjectsError } = useUpdateProjects();
  const { mutate: updateCertifications, isPending: isUpdatingCertifications, error: updateCertificationsError } = useUpdateCertifications();
  const { mutate: updateSkills, isPending: isUpdatingSkills, error: updateSkillsError } = useUpdateSkills();
  const { mutate: updateOnboardingStep } = useUpdateOnboardingStep();
  // Fetch full portfolio data from the API
  const { data: fetchedPortfolio , isLoading: isLoadingPortfolio, error: fetchPortfolioError } = useGetFullPortfolio();

  const normalizeExperience = (exp) => {
    let city = exp.city || '';
    let country = exp.country || '';
    if (!city && !country && exp.location) {
      const parts = exp.location.split(',').map((s) => s.trim());
      city = parts[0] || '';
      country = parts[1] || '';
    }
    return {
      ...exp,
      job_title: exp.job_title || exp.role || exp.name || '',
      city,
      country,
      description: exp.description || '',
    };
  };

  const transformGeneratedContent = (content) => {
    let result = {};

    if (Array.isArray(content)) {
      // Legacy array format: [{model: "Experience", fields: {...}}, ...]
      content.forEach(item => {
        result[item.model] = item.fields;
      });
    } else if (content && typeof content === 'object') {
      // New flat format: {"Experience": [...], "Education": [...], ...}
      result = content;
    }

    if (Array.isArray(result.Experience)) {
      result.Experience = result.Experience.map(normalizeExperience);
    }
    if (Array.isArray(result.Certification)) {
      result.Certification = result.Certification.map(cert => ({
        ...cert,
        title: cert.title || cert.name || '',
      }));
    }
    // Normalize project fields: map name to title
    if (Array.isArray(result.Project)) {
      result.Project = result.Project.map(proj => {
        const projectSkills = proj.skills || proj.technologies || proj.tech_stack || [];
        return {
          ...proj,
          title: proj.title || proj.name || '',
          skills: Array.isArray(projectSkills)
            ? projectSkills.map(skill =>
                typeof skill === 'string' ? skill : skill?.name,
              ).filter(Boolean)
            : [],
        };
      });
    }
    // Normalize profile: map full_name to username
    if (result.Profile) {
      if (result.Profile.full_name && !result.Profile.username) {
        result.Profile.username = result.Profile.full_name;
      }
    }
    // Normalize skills: convert strings to objects
    if (Array.isArray(result.Skill)) {
      result.Skill = result.Skill.map(s => typeof s === 'string' ? { name: s } : s);
    }
    return result;
  };

  const getProjectSkillMatches = (project, profileSkills) => {
    const projectText = `${project.title || ''} ${project.description || ''}`.toLowerCase();
    return profileSkills.filter((skill) => {
      const normalizedSkill = skill.toLowerCase().trim();
      if (!normalizedSkill || normalizedSkill.length < 2) return false;
      const escapedSkill = normalizedSkill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|\\W)${escapedSkill}(?=\\W|$)`, 'i').test(projectText);
    });
  };

  const parseResumeInfo = () => {
    const raw = JSON.parse(localStorage.getItem('resume-info'));
    if (raw) {
      const info = transformGeneratedContent(raw);
      setResumeInfo(info);
    }
  };

  const loadSavedPage = (portfolio) => {
    // Check if onboarding is completed - redirect to profile view
    if (portfolio?.profile?.onboarding_step === 'completed') {
      navigate('/default/profile');
      return 'profile';
    }
    
    // Get the current onboarding step from the profile
    const currentStep = portfolio?.profile?.onboarding_step;
    if (currentStep && ['profile', 'education', 'experience', 'projects', 'certifications', 'skills'].includes(currentStep)) {
      return currentStep;
    }
    return checkPageAndNavigate(portfolio);
  };

  useEffect(() => {

    if (fetchedPortfolio && !hasLoadedInitialPortfolio.current) {
      const nextPage = loadSavedPage(fetchedPortfolio);
      setCurrentPage(nextPage);
      hasLoadedInitialPortfolio.current = true;
    }
    const pid = user?.profile?.profile_id || user?.profile?.id;
    if (pid)
      setProfileId(pid);
    else if (!user)
      navigate('/login')

  },[fetchedPortfolio, user])

  const saveProfile = (data) => {
    updateProfile(data, {
      onSuccess: (resp) => {
        const newProfileId = resp.data?.profile_id || resp.data?.id;
        if (newProfileId) {
          setProfileId(newProfileId);
          const nextPage = 'education';
          setCurrentPage(nextPage);
          // Backend will auto-update onboarding_step through the profile update
        } else {
          toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Profile ID not found in response', life: 3000 });
        }
      },
      onError: (error) => {
        toastRef.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.detail || 'Failed to save profile', life: 3000 });
      },
    });
  };

  const injectProfileId = (data) => {
    if (!profileId) {
      // Profile ID is missing! This means user skipped the profile form
      throw new Error('Profile ID is missing. Please save your profile information first.');
    }
    return data.map((item) => ({
      ...item,
      profile: profileId, // Inject profile ID
    }));
  };

  const saveEducations = (data) => {
    try {
      updateEducation(injectProfileId(data), {
        onSuccess: () => {
          const nextPage = 'experience';
          setCurrentPage(nextPage);
          updateOnboardingStep('experience');
        },
        onError: (error) => {
          toastRef.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.detail || 'Failed to save educations', life: 3000 });
        },
      });
    } catch (err) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message, life: 3000 });
    }
  };

  const saveExperience = (data) => {
    try {
      updateExperience(injectProfileId(data), {
        onSuccess: () => {
          const nextPage = 'projects';
          setCurrentPage(nextPage);
          updateOnboardingStep('projects');
        },
        onError: (error) => {
          toastRef.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.detail || 'Failed to save experiences', life: 3000 });
        },
      });
    } catch (err) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message, life: 3000 });
    }
  };
  const saveProjects = (data) => {
    try {
      const profileSkills = (resumeInfo?.Skill || [])
        .map((skill) => typeof skill === 'string' ? skill : skill?.name)
        .filter(Boolean);
      const projects = data.map(({ skills, ...project }) => ({
        ...project,
        new_skills: Array.isArray(skills) && skills.length > 0
          ? skills.filter(Boolean)
          : getProjectSkillMatches(project, profileSkills),
      }));
      updateProjects(injectProfileId(projects), {
        onSuccess: () => {
          const nextPage = 'certifications';
          setCurrentPage(nextPage);
          updateOnboardingStep('certifications');
        },
        onError: (error) => {
          toastRef.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.detail || 'Failed to save projects', life: 3000 });
        },
      });
    } catch (err) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message, life: 3000 });
    }
  };
  const saveCertifications = (data) => {
    try {
      updateCertifications(injectProfileId(data), {
        onSuccess: () => {
          const nextPage = 'skills';
          setCurrentPage(nextPage);
          updateOnboardingStep('skills');
        },
        onError: (error) => {
          toastRef.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.detail || 'Failed to save certifications', life: 3000 });
        },
      });
    } catch (err) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err.message, life: 3000 });
    }
  };
  const saveSkills = (data) => {
    updateSkills(injectProfileId(data), {
      onSuccess: () => {
        localStorage.removeItem('onboarding-current-page');
        localStorage.removeItem('resume-info');
        updateOnboardingStep('completed');
        navigate('/default/profile')
      },
      onError: (error) => {
        toastRef.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.detail || 'Failed to save skills', life: 3000 });
      },
    });
  };
  useEffect(() => {
    // if (!isLoadingPortfolio) {
      // If profileId is available, fetch profile data from the API
      parseResumeInfo();
    // }
  }, []);

  const checkPageAndNavigate = (portfolio) => {
    if (!portfolio.profile.profile_name || !portfolio.profile.phone || !portfolio.profile.bio || !portfolio.profile.country || !portfolio.profile.username || !portfolio.profile.email)
      return "profile";
    if (portfolio.educations.length === 0)
      return "education";
    if (portfolio.experiences.length === 0)
      return "experience";
    if (portfolio.skills.length === 0)
      return "skills";
    navigate("/default/profile");
    return "profile"; // fallback to prevent undefined
  };

  return (
    <Background>
      <FormTitle>User Profile</FormTitle>
      {isLoadingPortfolio ? <Loading /> :
        <Card width="fit-content">
          {updateError && <p>Error: {updateError.message}</p>}
          {/* {isLoadingPortfolio && <p>Loading portfolio...</p>} */}
          {fetchPortfolioError && <p>Error: {fetchPortfolioError.message}</p>}
          {currentPage === "profile" && (
            <ProfileForm
              loading={isUpdatingProfile}
              onBack={() => navigate('/onboarding/choose-profile')}
              onSave={saveProfile}
              defaultData={
                resumeInfo && {
                  ...resumeInfo['Profile'],
                  ...resumeInfo['User'],
                  email: user?.email || resumeInfo?.User?.email,
                }
              }
            />
          )}
          {currentPage === "education" && (
            <Education loading={isUpdatingEducation} onBack={() => setCurrentPage('profile')} onSave={saveEducations} defaultData={resumeInfo && resumeInfo['Education']} />
          )}
          {currentPage === "experience" && (
            <Experience loading={isUpdatingExperience} onBack={() => setCurrentPage('education')} onSave={saveExperience} defaultData={resumeInfo && resumeInfo['Experience']} />
          )}
          {currentPage === "projects" && (
            <Projects loading={isUpdatingProjects} onBack={() => setCurrentPage('experience')} onSave={saveProjects} defaultData={resumeInfo && resumeInfo['Project']} />
          )}
          {currentPage === "certifications" && (
            <Certifications loading={isUpdatingCertifications} onBack={() => setCurrentPage('projects')} onSave={saveCertifications} defaultData={resumeInfo && resumeInfo['Certification']} />
          )}
          {currentPage === "skills" && (
            <Skills loading={isUpdatingSkills} onBack={() => setCurrentPage('certifications')} onSave={saveSkills} defaultData={resumeInfo && resumeInfo['Skill']} />
          )}
        </Card>
      }
    </Background>
  );
};

export default Form;
